import {
    BreachEvent,
    createRegisterableComponent,
    DataLayerEventName,
    useToggleState,
} from '@news-mono/web-common'
import { TheWestSections } from '@west-australian-newspapers/publication-types'
import React, { useEffect } from 'react'
import { MessageBanner } from '../../banners/MessageBanner/MessageBanner'
import { useFeatureForSection } from '../../feature-toggling'
import { packageOfferDetails, useUpdateBreachWallContext } from './common'
import { BreachWallContent } from './components/BreachNotificationWrapper/BreachNotification/BreachWallContent/BreachWallContent'
import {
    StyledABTestBannerWrap,
    StyledMessage,
    StyledTerms,
    StyledTitle,
} from './components/BreachNotificationWrapper/BreachNotification/BreachWallContent/BreachWallContent.styled'
import { BreachWallContentType } from './components/BreachNotificationWrapper/BreachNotificationWrapper'
import { WestRewardsMessage } from './components/BreachNotificationWrapper/shared-components/WestRewardsMessage/WestRewardsMessage'

export interface BreachScreenRegistrationProps {
    bannerMessage?: string
    breachCreativeName: string
    breachDeal: string
    breachType: BreachWallContentType
    title: string
    message?: string
    terms: string
}

export const BreachScreenRegistration = createRegisterableComponent(
    'breachscreen',
    (props: BreachScreenRegistrationProps, services) => {
        const is50percentOffDeal =
            props.breachDeal === 'COVIDDIG' || props.breachDeal === 'GOVAN50'

        const is30percentOffDeal =
            props.breachDeal === 'BIKEDI30' || props.breachDeal === 'SCHOOL30'

        const eventPayload: BreachEvent['payload'] = {
            paywallType: 'freemium',
            leadDepth: 0,
            creative: 'registration wall - launch',
            breachScreenType: 'Registration - Inline',
            popupMethod: 'auto',
            deal: '',
            pricepoint: [],
        }

        const isNewDesign2022 = useFeatureForSection({
            feature: 'tp-subscription-new-breach-package-background',
            sections: TheWestSections.default,
        })

        //required for subscribe with google button
        useUpdateBreachWallContext({
            publication: undefined,
            requiredAccessLevel: 'subscriber',
            breachCreativeName: 'TW_Subscribe_With_Google_Campaign',
        })

        const abTestVariation = useToggleState()?.['breach-ab-test-1'] || 'a'
        let abBreachDeal: string | undefined
        let abBreachType: BreachWallContentType = props.breachType

        if (abTestVariation) {
            if (abTestVariation === 'a') {
                abBreachDeal = props.breachDeal
                abBreachType = props.breachType
            }
            if (abTestVariation === 'b') {
                abBreachDeal = 'DIG1X4'
                abBreachType = 'abtest-b-1x4'
            }
            if (abTestVariation === 'c') {
                abBreachDeal = 'DIG50CX8'
                abBreachType = 'abtest-c-50cx8'
            }
            if (abTestVariation === 'd') {
                abBreachDeal = 'DIG50X8'
                abBreachType = 'abtest-d-50px8'
            }
        }

        const subscriptionPackages = packageOfferDetails(
            props.breachDeal === 'epaper-regional'
                ? 'digital-only'
                : abBreachType
                ? abBreachType
                : 'default',
        )
        // Fire the available event
        useEffect(() => {
            services.onEvent({
                type: DataLayerEventName.breachScreenAvailable,
                originator: 'breachscreen',
                payload: eventPayload,
            })
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [])

        return (
            <div data-testid="BreachScreen">
                {props.bannerMessage && (
                    <MessageBanner
                        icon="subscriber"
                        connector="solid-arrow"
                        onEvent={services.onEvent}
                    >
                        <p>{props.bannerMessage}</p>
                    </MessageBanner>
                )}

                <BreachWallContent
                    breachCreativeName={props.breachCreativeName}
                    breachType={abBreachType ? abBreachType : props.breachType}
                    breachDeal={abBreachDeal ? abBreachDeal : props.breachDeal}
                    onEvent={services.onEvent}
                    title={() => {
                        if (is50percentOffDeal) {
                            return (
                                <React.Fragment>
                                    <StyledTitle>Limited Offer</StyledTitle>
                                    <StyledMessage>
                                        <p>Save over 50% for 8 weeks*</p>
                                    </StyledMessage>
                                </React.Fragment>
                            )
                        }
                        if (is30percentOffDeal) {
                            return (
                                <React.Fragment>
                                    <StyledTitle>
                                        Limited Time Offer
                                    </StyledTitle>
                                    <StyledMessage>
                                        <p>
                                            Subscribe now to save 30%* and enjoy
                                            unlimited access to investigative
                                            video series, breaking news, sport,
                                            business and lifestyle direct to
                                            your device
                                        </p>
                                    </StyledMessage>
                                </React.Fragment>
                            )
                        }
                        // The ABTEST banners
                        if (abTestVariation == 'b') {
                            return (
                                <React.Fragment>
                                    <StyledABTestBannerWrap>
                                        <StyledTitle>
                                            First month for $1
                                        </StyledTitle>
                                        <StyledMessage>
                                            <p>on Everyday Digital</p>
                                            <p className="abtest-terms">
                                                T's & C's apply
                                            </p>
                                        </StyledMessage>
                                    </StyledABTestBannerWrap>
                                </React.Fragment>
                            )
                        }
                        if (abTestVariation == 'c') {
                            return (
                                <React.Fragment>
                                    <StyledABTestBannerWrap>
                                        <StyledTitle>50c A DAY</StyledTitle>
                                        <StyledMessage>
                                            <p>
                                                Everyday Digital for first 8
                                                weeks
                                            </p>
                                            <p className="abtest-terms">
                                                T's & C's apply
                                            </p>
                                        </StyledMessage>
                                    </StyledABTestBannerWrap>
                                </React.Fragment>
                            )
                        }
                        if (abTestVariation == 'd') {
                            return (
                                <React.Fragment>
                                    <StyledABTestBannerWrap>
                                        <StyledTitle>50% OFF</StyledTitle>
                                        <StyledMessage>
                                            <p>
                                                Everyday Digital for first 8
                                                weeks
                                            </p>
                                            <p className="abtest-terms">
                                                T's & C's apply
                                            </p>
                                        </StyledMessage>
                                    </StyledABTestBannerWrap>
                                </React.Fragment>
                            )
                        }

                        return (
                            <React.Fragment>
                                <StyledTitle>{props.title}</StyledTitle>
                                {props.message && (
                                    <StyledMessage>
                                        <p>{props.message}</p>
                                    </StyledMessage>
                                )}
                            </React.Fragment>
                        )
                    }}
                    terms={() => {
                        if (is50percentOffDeal || is30percentOffDeal) {
                            return (
                                <StyledTerms>
                                    *Valid for new subscribers only.
                                    {` ${breachDealTerms(props.breachDeal)}`}
                                    <br />
                                    T&Cs apply
                                </StyledTerms>
                            )
                        }

                        return null
                    }}
                    mediaBanner={() => {
                        return null
                    }}
                    subscriptionPackages={subscriptionPackages}
                />
                {isNewDesign2022 && <WestRewardsMessage />}
            </div>
        )
    },
)

function breachDealTerms(deal: string) {
    switch (deal) {
        case 'SCHOOL30':
            return 'Offer ends 23 November 2021.'
        case 'BIKEDI30':
            return 'Offer ends 21 November 2021.'
        case 'GOVAN50':
            return 'Offer ends 20 February 2021.'
        case 'BRE1DISC':
            return 'Offer ends 2 May 2021.'
        default:
            return ''
    }
}
